import React, { useState } from 'react';
import { FiX, FiFile, FiUploadCloud, FiArrowRight, FiCheck, FiRefreshCw, FiDownload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const JPE_FIELDS = [
  { key: 'name', label: 'Nom du produit', required: true },
  { key: 'salePrice', label: 'Prix de vente', required: true },
  { key: 'purchasePrice', label: 'Prix d\'achat', required: false },
  { key: 'quantity', label: 'Quantité en stock', required: false },
  { key: 'barcode', label: 'Code-barres', required: false },
  { key: 'category', label: 'Catégorie', required: false },
  { key: 'lowStockThreshold', label: 'Seuil d\'alerte stock', required: false },
];

const ImportProductsModal = ({ isOpen, onClose, onImport }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [processedProducts, setProcessedProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const resetState = () => {
    setStep(1);
    setFile(null);
    setRawData([]);
    setHeaders([]);
    setMapping({});
    setProcessedProducts([]);
    setLoading(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseFile(selectedFile); 
    }
  };

  const parseFile = (fileToParse) => {
    setLoading(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = event.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        const fileHeaders = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];
        const fileData = XLSX.utils.sheet_to_json(worksheet);

        setHeaders(fileHeaders);
        setRawData(fileData);
        autoMapHeaders(fileHeaders);
        setStep(2);
      } catch (error) {
        toast.error("Erreur lors de la lecture du fichier. Assurez-vous que c'est un format valide (Excel, CSV).");
        console.error(error);
        resetState();
      } finally {
        setLoading(false);
      }
    };
    reader.readAsBinaryString(fileToParse);
  };
  
  const autoMapHeaders = (fileHeaders) => {
    const newMapping = {};
    const commonMappings = {
      name: ['nom', 'désignation', 'article', 'produit'],
      salePrice: ['prix vente', 'pv', 'prix ttc'],
      purchasePrice: ['prix achat', 'pa', 'prix ht'],
      quantity: ['quantité', 'qte', 'stock'],
      barcode: ['code-barres', 'barcode', 'ean'],
      category: ['catégorie', 'rayon'],
      lowStockThreshold: ['seuil', 'alerte'],
    };

    JPE_FIELDS.forEach(field => {
      const foundHeader = fileHeaders.find(h => 
        commonMappings[field.key].some(term => h.toLowerCase().includes(term))
      );
      if (foundHeader) {
        newMapping[field.key] = foundHeader;
      }
    });
    setMapping(newMapping);
  };

  const handleMappingChange = (jpeField, fileHeader) => {
    setMapping(prev => ({ ...prev, [jpeField]: fileHeader }));
  };

  const handleConfirmMapping = () => {
    const requiredFieldsMet = JPE_FIELDS.filter(f => f.required).every(f => mapping[f.key] && mapping[f.key] !== 'ignore');
    if (!requiredFieldsMet) {
      toast.error("Veuillez faire correspondre tous les champs obligatoires (Nom, Prix de vente, Prix d'achat).");
      return;
    }

    const transformedProducts = rawData.map(row => {
      const product = {};
      JPE_FIELDS.forEach(field => {
        const fileHeader = mapping[field.key];
        if (fileHeader && fileHeader !== 'ignore') {
          product[field.key] = row[fileHeader];
        }
      });
      return product;
    }).filter(p => p.name && p.salePrice); // On ne filtre plus sur le prix d'achat

    setProcessedProducts(transformedProducts);
    setStep(3);
  };

  const handleImportClick = () => {
    if (processedProducts.length > 0) {
      onImport(processedProducts);
    } else {
      toast.error("Aucun produit à importer. Veuillez sélectionner un fichier valide.");
    }
  };

  const handleDownloadTemplate = () => {
    // Utiliser le point-virgule comme délimiteur pour une meilleure compatibilité avec Excel (fr)
    const delimiter = ';';
    const headers = JPE_FIELDS.map(f => `"${f.label}"`).join(delimiter);
    const exampleRow = JPE_FIELDS.map(f => {
      if (f.key === 'name') return '"Savon de Marseille"';
      if (f.key === 'salePrice') return '1500';
      if (f.key === 'purchasePrice') return '1200';
        if (f.key === 'quantity') return '50';
        if (f.key === 'barcode') return '123456789JPND';
        if (f.key === 'category') return 'Hygiène';
        if (f.key === 'lowStockThreshold') return '5';
      return '';
    }).join(delimiter);

    // Ajouter un BOM (Byte Order Mark) pour garantir que l'UTF-8 est bien lu par Excel
    const bom = '\uFEFF';
    const csvContent = bom + headers + '\n' + exampleRow;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "modele_import_jaapande.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Importer des Produits</h3>
          <button onClick={handleClose}><FiX size={24} /></button>
        </div>

        {step === 1 && (
          <div className="flex flex-col items-center justify-center w-full h-full p-4">
             <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
               <div className="flex flex-col items-center justify-center pt-5 pb-6">
                 <FiUploadCloud className="w-12 h-12 mb-4 text-gray-400" />
                 <p className="mb-2 text-lg text-gray-500"><span className="font-semibold">Cliquez pour choisir un fichier</span></p>
                 <p className="text-sm text-gray-500">ou glissez-déposez votre fichier Excel/CSV</p>
               </div>
               <input type="file" className="hidden" onChange={handleFileChange} accept=".xlsx, .xls, .csv" />
             </label>
             {loading && <p className="mt-4">Analyse du fichier...</p>}
             <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 mb-2">Pas de fichier ? Téléchargez notre modèle pour commencer.</p>
                <button onClick={handleDownloadTemplate} className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg flex items-center mx-auto">
                    <FiDownload className="mr-2"/>
                    Télécharger le modèle
                </button>
             </div>
          </div>
        )}

        {/* Étape 2: Mapping */}
        {step === 2 && (
          <div className="flex flex-col flex-grow overflow-hidden">
            <p className="mb-4 text-gray-600">Faites correspondre les colonnes de votre fichier avec les champs de Jaapand-e.</p>
            <div className="overflow-y-auto flex-grow pr-2">
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th className="text-left py-2">Champ Jaapand-e</th><th className="text-left py-2">Colonne de votre fichier</th></tr></thead>
                <tbody>
                  {JPE_FIELDS.map(field => (
                    <tr key={field.key} className="border-b">
                      <td className="py-3 font-medium">{field.label} {field.required && <span className="text-red-500">*</span>}</td>
                      <td className="py-3">
                        <select value={mapping[field.key] || 'ignore'} onChange={(e) => handleMappingChange(field.key, e.target.value)} className="w-full p-2 border rounded bg-white">
                          <option value="ignore">-- Ne pas importer --</option>
                          {headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-auto flex justify-between items-center pt-4 border-t">
              <button onClick={resetState} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg flex items-center"><FiRefreshCw className="mr-2"/>Recommencer</button>
              <button onClick={handleConfirmMapping} className="bg-brand-blue hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg flex items-center">Valider et Prévisualiser <FiArrowRight className="ml-2"/></button>
            </div>
          </div>
        )}

        {/* Étape 3: Preview & Confirm */}
        {step === 3 && (
          <div className="flex flex-col flex-grow overflow-hidden">
            <p className="mb-4 text-green-700 font-semibold"><FiCheck className="inline mr-2"/> {processedProducts.length} produits prêts à être importés. Voici un aperçu :</p>
            <div className="overflow-auto flex-grow border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 sticky top-0"><tr className="border-b">{JPE_FIELDS.map(f => <th key={f.key} className="p-2 text-left font-semibold">{f.label}</th>)}</tr></thead>
                <tbody>
                  {processedProducts.slice(0, 5).map((product, index) => (
                    <tr key={index} className="border-b">
                      {JPE_FIELDS.map(field => <td key={field.key} className="p-2 truncate max-w-xs">{String(product[field.key] || '')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-auto flex justify-between items-center pt-4 border-t">
              <button onClick={() => setStep(2)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Retour</button>
              <button onClick={handleImportClick} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg flex items-center"><FiFile className="mr-2"/>Lancer l'importation</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportProductsModal;